// src/hooks/useFieldOpsZones.js
// --- UPDATE (Field Ops · Zones v1) — NEW FILE ---------------------------------------------
// Live zone / constituency data for Field Operations.
//   • Loads GET /field-ops/zones-intel/overview (zones, work orders, technicians, ML).
//   • Listens to the FlowSense WebSocket: field_ops_zone_update (server monitor — applied
//     instantly to the zone counters, then a debounced full refresh), plus the existing work-order
//     and location events (work_order_created / _updated / _completed, work_order_events,
//     technician_location_update, location_update, sla_grade_recorded).
//   • Bursts of events are coalesced (one refetch per DEBOUNCE_MS), in-flight requests are
//     aborted when a newer one starts, and polling pauses while the tab is hidden.
//   • Fallback polling: every 60 s while the socket is down, every 3 min while it is up.
// --- END UPDATE ---------------------------------------------------------------------------
import { useCallback, useEffect, useRef, useState } from 'react';
import webSocketService from '../services/websocketService';
import fieldOpsZoneApi from '../services/fieldOpsZoneApi';

const DEBOUNCE_MS = 1500;
const POLL_DOWN_MS = 60000;
const POLL_UP_MS = 180000;
export const LIVE_EVENTS = Object.freeze([
    'work_order_created', 'work_order_updated', 'work_order_completed', 'work_order_events',
    'technician_location_update', 'location_update', 'sla_grade_recorded',
]);
const payloadOf = (msg) => (msg && (msg.payload || msg.data)) || msg || {};

export default function useFieldOpsZones({ enabled = true } = {}) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [wsStatus, setWsStatus] = useState(() => (webSocketService.getStatus ? webSocketService.getStatus().status : 'idle'));
    const [lastEvent, setLastEvent] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);
    const mounted = useRef(true);
    const abortRef = useRef(null);
    const debounceRef = useRef(null);
    const pendingRefresh = useRef(false);

    const load = useCallback(async ({ refresh = false, quiet = false } = {}) => {
        if (!enabled) return;
        if (typeof document !== 'undefined' && document.hidden && quiet) { pendingRefresh.current = true; return; }
        abortRef.current?.abort?.();
        const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        abortRef.current = ctrl;
        if (!quiet) setIsLoading(true);
        try {
            const o = await fieldOpsZoneApi.overview({ refresh, signal: ctrl?.signal });
            if (!mounted.current || abortRef.current !== ctrl) return;
            setData(o); setError(null); setUpdatedAt(new Date());
        } catch (e) {
            if (e?.name === 'AbortError' || !mounted.current) return;
            setError(e.status === 404 ? 'Zone intelligence is not on this server yet — mount routes/fieldOpsZoneRoutes.js at /api/v1/field-ops/zones-intel.'
                : e.status === 403 ? 'Your role cannot view zone operations data.' : e.message || 'Could not load zone data.');
        } finally {
            if (mounted.current && abortRef.current === ctrl) setIsLoading(false);
        }
    }, [enabled]);

    const scheduleRefresh = useCallback(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => { if (mounted.current) load({ quiet: true }); }, DEBOUNCE_MS);
    }, [load]);

    // initial load
    useEffect(() => {
        mounted.current = true;
        load();
        return () => { mounted.current = false; abortRef.current?.abort?.(); clearTimeout(debounceRef.current); };
    }, [load]);

    // websocket
    useEffect(() => {
        if (!enabled) return undefined;
        try { if (!webSocketService.isConnected && typeof webSocketService.connect === 'function') webSocketService.connect(); } catch (_) { /* app-level connection */ }
        const onZone = (msg) => {
            const p = payloadOf(msg);
            if (!mounted.current) return;
            setLastEvent({ type: 'field_ops_zone_update', reason: p.reason || null, at: new Date() });
            if (Array.isArray(p.zones)) {
                const byId = new Map(p.zones.map((z) => [String(z.id), z]));
                setData((d) => (d ? {
                    ...d,
                    totals: p.totals || d.totals,
                    score: p.score ?? d.score,
                    zones: d.zones.map((z) => {
                        const u = byId.get(String(z.id));
                        return u ? { ...z, color: u.color || z.color, stats: { ...z.stats, open: u.open, critical: u.critical, overdue: u.overdue, techs_online: u.techs_online }, risk: { ...z.risk, score: u.risk } } : z;
                    }),
                } : d));
            }
            scheduleRefresh();
        };
        const onLive = (msg) => {
            if (!mounted.current) return;
            setLastEvent({ type: msg?.type || 'event', at: new Date() });
            scheduleRefresh();
        };
        const onStatus = (msg) => { if (mounted.current && msg?.status) setWsStatus(msg.status); };
        const unsubs = [
            webSocketService.subscribe('field_ops_zone_update', onZone),
            webSocketService.subscribe('status', onStatus),
            ...LIVE_EVENTS.map((evt) => webSocketService.subscribe(evt, onLive)),
        ];
        return () => {
            unsubs.forEach((u) => { try { if (typeof u === 'function') u(); } catch (_) { /* */ } });
            webSocketService.unsubscribe?.('field_ops_zone_update', onZone);
            webSocketService.unsubscribe?.('status', onStatus);
            LIVE_EVENTS.forEach((evt) => webSocketService.unsubscribe?.(evt, onLive));
        };
    }, [enabled, scheduleRefresh]);

    // fallback polling + resume when the tab becomes visible
    useEffect(() => {
        if (!enabled) return undefined;
        const ms = wsStatus === 'connected' ? POLL_UP_MS : POLL_DOWN_MS;
        const id = setInterval(() => load({ quiet: true }), ms);
        const onVis = () => { if (!document.hidden && pendingRefresh.current) { pendingRefresh.current = false; load({ quiet: true }); } };
        if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVis);
        return () => { clearInterval(id); if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVis); };
    }, [enabled, wsStatus, load]);

    const refresh = useCallback(() => load({ refresh: true }), [load]);
    const patchZone = useCallback(async (id, body) => {
        const z = await fieldOpsZoneApi.updateZone(id, body);
        if (z && mounted.current) setData((d) => (d ? { ...d, zones: d.zones.map((x) => (String(x.id) === String(id) ? { ...x, ...z } : x)) } : d));
        return z;
    }, []);

    return { data, isLoading, error, wsStatus, lastEvent, updatedAt, refresh, reload: load, patchZone, isLive: wsStatus === 'connected' };
}
